import { useEffect, useState } from 'react';
import { FC } from 'react';
import { Grid, GridCellProps, GridColumn, GridDetailExpandChangeEvent } from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State as ODataState } from '@progress/kendo-data-query';
import myCustomersApi from '../../api/MyCustomersApi';
import { Cart } from 'src/types/customerCartList';
import { Box, Card, CircularProgress } from '@mui/material';
import CustomerCartDetail from './CustomerCartDetail';
import useAuth from '../../hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import CustomerUrlCell from '../grid/CustomerUrlCell';
import FilterOperators from '../grid/FilterOperators';
import ColumnMenu from '../grid/ColumnMenu';
import useSettings from 'src/hooks/useSettings';
import { SETTING_KEYS } from 'src/constants';
import { DetailExpandDescriptor } from '@progress/kendo-react-data-tools';
import { getPageSizeSetting } from 'src/utils/settingsHelper';

const CustomerCartSummary: FC<React.PropsWithChildren<unknown>> = () => {
  const CustomerCartSummaryPageSizeKey = SETTING_KEYS.CustomerCartSummaryPageSize;
  const { user } = useAuth();
  const [carts, setCarts] = useState<Cart[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const savePagingSettings = useSettings((state) => state.savePagingSettings);
  const userSettings = useSettings((state) => state.userSettings);

  const pageSize = getPageSizeSetting(userSettings, CustomerCartSummaryPageSizeKey, 50);
  const [expandedDetailState, setExpandedDetailState] = useState<DetailExpandDescriptor>({});
  const [defaultFilter] = useState<CompositeFilterDescriptor | FilterDescriptor>({
    filters: [
      { field: 'salesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'lawnAndGardenSalesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'mcSalesRepId', value: user.salesReps[0], operator: 'eq' },
      { field: 'plantSalesRepId', value: user.salesReps[0], operator: 'eq' },
    ],
    logic: 'or',
  });
  const [search] = useSearchParams();
  const [gridDataState, setGridDataState] = useState<ODataState>({
    filter: {
      filters: [defaultFilter],
      logic: 'and',
    },
    skip: 0,
    take: pageSize,
    sort: [{ field: 'customerName', dir: 'asc' }],
  });

  const handleDetailExpandChange = (event: GridDetailExpandChangeEvent) => {
    setExpandedDetailState(event.detailExpand);
  };

  const loadCustomerCarts = async () => {
    setIsLoading(true);
    myCustomersApi
      .getCustomerCartSummary(gridDataState)
      .then((response) => {
        setCarts(response.value);
        setTotal(response['@odata.count']);
        setIsLoading(false);
      })
      .catch(() => {
        setCarts([]);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadCustomerCarts();
  }, [gridDataState]);

  useEffect(() => {
    const filters: (CompositeFilterDescriptor | FilterDescriptor)[] = [];
    if (search.get('allcustomers') !== 'true') filters.push(defaultFilter);
    const newDataState: ODataState = {
      ...gridDataState,
      filter: {
        filters: filters,
        logic: 'and',
      },
      skip: 0,
      take: pageSize,
      sort: [{ field: 'customerName', dir: 'asc' }],
    };

    setGridDataState(newDataState);
  }, [search]);

  const customerUrlCell = (cellProps: GridCellProps) => <CustomerUrlCell {...cellProps} />;

  const handlePageChange = (e) => {
    const newPageSize = e.page.take;
    setGridDataState((prevState) => ({
      ...prevState,
      skip: e.page.skip,
      take: newPageSize,
    }));
    savePagingSettings(newPageSize, CustomerCartSummaryPageSizeKey);
  };

  return (
    <div className="progress-table-container">
      {isLoading && <CircularProgress></CircularProgress>}
      <Card>
        <Box
          sx={{
            minWidth: 500,
            overflow: 'none',
          }}
        ></Box>
        <Grid
          data={carts}
          {...gridDataState}
          pageable={{
            buttonCount: 4,
            pageSizes: [20, 50, 100, 500],
            pageSizeValue: pageSize,
          }}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          total={total}
          dataItemKey="customerName"
          sortable
          filterOperators={FilterOperators}
          columnMenu={ColumnMenu}
          resizable={true}
          scrollable="scrollable"
          detail={CustomerCartDetail}
          detailExpand={expandedDetailState}
          onDetailExpandChange={handleDetailExpandChange}
          onDataStateChange={(e) => setGridDataState(e.dataState)}
        >
          <GridColumn field="customerName" width={260} cells={{ data: customerUrlCell }} title="Customer Name" />
          <GridColumn field="customerNumber" width={150} title="Customer Number" />
          <GridColumn field="city" title="City" width={140} />
          <GridColumn field="state" title="State" width={60} />
          <GridColumn field="userCount" title="User Count" width={90} />
          <GridColumn field="totalInCart" title="Total In Cart" format="{0:c}" width={90} />
        </Grid>
      </Card>
    </div>
  );
};

export default CustomerCartSummary;
